<style lang="scss" scoped>
.product-detail {
    min-height: calc(var(--vh, 1vh) * 100 - var(--headerHeight));

    button[disabled='disabled'] {
        pointer-events: none;
        opacity: 0.6;
    }

    .decrease {
        height: 66px;
        width: 66px;
        border: 1px solid #aeb2bb;
        border-radius: 4px;
        background-color: #ffffff;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        font-size: 0.875rem;

        @apply hover:bg-blue hover:border-blue hover:text-white transition-colors;
    }

    .add-amount {
        height: 66px;
        width: calc(100% - 148px);
        border: 1px solid #aeb2bb;
        border-radius: 4px;

        @media screen and (min-width: 1024px) {
            width: 80px;
        }

        &:focus {
            outline: none;
        }
    }

    .increase {
        height: 66px;
        width: 66px;
        border: 1px solid #aeb2bb;
        border-radius: 4px;
        background-color: #ffffff;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        font-size: 0.875rem;

        @apply hover:bg-blue hover:border-blue hover:text-white transition-colors;
    }

    .surface-calculator {
        input {
            height: 59px;
            width: 100%;
            border: 1px solid #e1e1e1;
            border-radius: 4px;

            &:focus {
                outline: none;
            }
        }

        .surface {
            input {
                padding-right: 60px;
            }
            &:after {
                content: 'm²';
                position: absolute;
                color: #afafaf;
                font-weight: 500;
                pointer-events: none;
                right: 24px;
                top: 50%;
                transform: translateY(-50%);
            }
        }
    }

    table {
        th,
        td {
            min-width: 120px;

            @media screen and (min-width: 1024px) {
                min-width: 150px;
            }
        }

        @media screen and (min-width: 1024px) {
            table-layout: fixed;
        }
    }

    .component-fade-enter-active,
    .component-fade-leave-active {
        transition: opacity 0.3s ease;
    }
    .component-fade-enter,
    .component-fade-leave-to {
        opacity: 0;
    }
}
</style>

<template>
    <div class="product-detail flex">
        <transition name="component-fade" mode="out-in">
            <div v-if="ready && product != null" key="ready" class="flex-1 pt-24">
                <div class="container">
                    <div class="row lg:mb-24 lg:items-end mb-16">
                        <div class="w-full" :class="firstQuestionsSections != null ? 'lg:w-8/12' : ''">
                            <div class="h-30 no-scrollbars relative w-full overflow-scroll">
                                <breadcrumbs class="absolute top-0 left-0" :breadcrumbs="breadcrumbs"></breadcrumbs>
                            </div>

                            <h1 class="h4" v-html="product.name"></h1>
                        </div>
                        <div class="lg:flex justify-end hidden w-4/12" v-if="firstQuestionsSections != null">
                            <a :href="`#questions_${orderedSections.indexOf(firstQuestionsSections)}`" v-scroll-to="{ el: `#questions_${orderedSections.indexOf(firstQuestionsSections)}`, offset: -$root.scrollOffset }" class="btn btn-rounded btn-lg btn-outline-blue">{{ translations.button_questions_label }}</a>
                        </div>
                    </div>

                    <div class="md:flex-row md:items-center md:mb-40 flex flex-col mb-24">
                        <div v-if="false" class="lg:mb-0 flex items-center mb-8">
                            <score-stars class="mr-16" :score="4"></score-stars>
                            <span class="text-14 text-gray-400">23 beoordelingen</span>
                        </div>
                        <div class="md:flex-row flex flex-col">
                            <template v-for="(usp, index) in translations.product_usps">
                                <span class="text-14 last:mb-0 md:mb-0 mb-8 text-gray-400" :key="`description_${index}`">{{ usp.description }}</span>
                                <span v-if="index !== translations.product_usps.length - 1" class="text-14 md:inline-block hidden mx-8 text-gray-400" :key="`bull_${index}`">&bull;</span>
                            </template>
                        </div>
                    </div>

                    <div class="md:hidden mb-40" v-if="firstQuestionsSections != null">
                        <a :href="`#questions_${orderedSections.indexOf(firstQuestionsSections)}`" v-scroll-to="{ el: `#questions_${orderedSections.indexOf(firstQuestionsSections)}`, offset: -$root.scrollOffset }" class="btn btn-rounded btn-lg btn-outline-blue">{{ translations.button_questions_label }}</a>
                    </div>

                    <div class="row mb-60 lg:mb-80">
                        <div class="md:w-7/12 lg:mb-0 w-full mb-24">
                            <media-gallery :media="orderedMedia"></media-gallery>
                        </div>
                        <div class="md:w-5/12 w-full">
                            <div class="md:pl-40">
                                <div class="product-description lg:mb-40 mb-32 font-medium text-gray-500" v-html="product.description"></div>

                                <hr class="lg:mb-32 mb-24" />

                                <div class="lg:mb-32 mb-24">
                                    <div class="last:mb-0 mb-16" v-for="option in productOptionsWithoutOppCalc" :key="option.key">
                                        <span v-if="option.filterable" class="text-14 lead inline-block mb-8 text-gray-400">{{ option.name }}</span>
                                        <div v-if="option.filterable" class="flex flex-wrap -mb-8">
                                            <a href="#" @click.prevent="changeVariant(option, value)" class="btn btn-sm btn-rounded last:mr-0 mb-8 mr-8" :class="activeVariant.content[option.key] == value ? 'btn-green' : 'btn-outline-gray-400'" v-for="(value, index) in getPossibleOptions(option)" :key="index">{{ value }}</a>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex items-end mb-8">
                                    <span class="text-40 mr-8 font-semibold">&euro; {{ getActiveVariantPrice($store.getters['pricesIncludingVat']) }}</span>
                                    <span class="text-12 pb-10 font-semibold text-gray-400" v-if="$store.getters['pricesIncludingVat']">{{ translations.vat_included }}</span>
                                    <span class="text-12 pb-10 font-semibold text-gray-400" v-else>{{ translations.vat_excluded }}</span>
                                </div>
                                <div class="flex items-end mb-16">
                                    <span class="text-12 mr-2 font-semibold text-gray-400">&euro; {{ getActiveVariantPrice(!$store.getters['pricesIncludingVat']) }}</span>
                                    <span class="text-10 font-semibold text-gray-400" v-if="!$store.getters['pricesIncludingVat']">{{ translations.vat_included }}</span>
                                    <span class="text-10 font-semibold text-gray-400" v-else>{{ translations.vat_excluded }}</span>
                                </div>

                                <div class="text-12 flex flex-wrap items-center font-semibold leading-tight text-gray-400">
                                    <span class="text-12 inline-block mx-8 mb-8" :class="activeVariantInStock ? 'text-green' : 'text-red'">&bull;</span>
                                    <span class="mb-8">{{ activeVariantInStock ? translations.in_stock : translations.not_in_stock }}</span>
                                    <template v-if="activeVariant.volume_prices != null && activeVariant.volume_prices.length > 1">
                                        <span class="text-10 inline-block mx-8 mb-8">&bull;</span>
                                        <span class="mb-8">Korting vanaf {{ activeVariant.volume_prices[1].from }} stuks</span>
                                        <span class="text-10 inline-block mx-8 mb-8">&bull;</span>
                                        <a href="#prijzen-afmetingen" v-scroll-to="{ el: '#prijzen-afmetingen', offset: -$root.scrollOffset }" class="hover:border-transparent mb-8 transition border-b border-gray-400">{{ translations.discover_discount }}</a>
                                    </template>
                                </div>

                                <div v-if="!showSurfaceCalculator" class="lg:mt-40 flex flex-wrap mt-16">
                                    <a href="#" class="decrease mr-8" @click.prevent="decreaseAmount" v-html="svg('minus')"></a>
                                    <input v-model="addAmount" type="number" class="add-amount mr-8 text-center appearance-none" />
                                    <a href="#" class="increase lg:mr-16" @click.prevent="increaseAmount" v-html="svg('plus')"></a>
                                    <button @click="addToCart" :disabled="!activeVariantInStock" class="btn btn-rounded btn-green lg:w-auto xl:mt-0 w-full mt-8">{{ translations.add_product_button }}</button>
                                </div>

                                <div v-else class="rounded-xs surface-calculator lg:mt-32 p-24 mt-16 bg-gray-100">
                                    <p class="text-20 mb-56 font-bold leading-tight">{{ translations.calculate_surface }}</p>
                                    <div class="flex items-center mb-16">
                                        <div class="surface relative flex flex-1">
                                            <span class="bottom-full text-14 absolute font-medium text-gray-500 transform -translate-y-16">{{ translations.surface }}</span>
                                            <input v-model="localSurface" @input="calculateNeededAmount" type="number" class="flex-1 appearance-none" />
                                        </div>
                                        <span class="text-14 inline-block mx-10 font-medium text-gray-500">=</span>
                                        <div class="relative flex flex-1">
                                            <span class="bottom-full text-14 absolute font-medium text-gray-500 transform -translate-y-16">{{ translations.pieces }}</span>
                                            <input v-model="addAmount" @input="calculateSurfaceOfAmount" type="number" class="flex-1 appearance-none" />
                                        </div>
                                    </div>
                                    <el-input v-model="applyCuttingLoss" type="checkbox" :label="translations.surface_calculator_disclaimer" class="mb-16"></el-input>
                                    <button @click="addToCart" :disabled="!activeVariantInStock" class="btn btn-rounded btn-green w-full">{{ translations.add_product_button }}</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mb-60 lg:mb-80">
                        <slot name="usps"></slot>
                    </div>

                    <div class="lg:flex hidden mb-16">
                        <a v-if="Object.keys(mappedOptions).length > 0" class="last:mr-0 hover:text-green mr-32 transition-colors" v-smooth-scroll="{ offset: -$root.headerHeight }" href="#prijzen-afmetingen">{{ translations.pricing_sizing }}</a>
                        <a v-for="(section, index) in orderedSections" :key="index" class="last:mr-0 hover:text-green mr-32 transition-colors" v-smooth-scroll="{ offset: -$root.headerHeight }" :href="`#${section.type}_${index}`">{{ section.content.title != '' ? section.content.title : translations[section.type] }}</a>
                    </div>
                </div>

                <hr class="lg:block hidden mb-40" />

                <div id="prijzen-afmetingen" class="mb-60 lg:mb-80 last:mb-0" v-if="Object.keys(mappedOptions).length > 0">
                    <div class="container">
                        <h2 class="h4 mb-32">{{ translations.pricing_sizing }}</h2>
                    </div>

                    <div class="w-screen pr-20 overflow-x-scroll">
                        <div class="container">
                            <table class="block w-full text-left">
                                <thead>
                                    <tr class="border-b border-gray-100">
                                        <template v-for="option in Object.keys(mappedOptions)">
                                            <th class="text-12 first:pl-8 pb-16 font-bold text-gray-500" :key="option">{{ mappedOptions[option] }}</th>
                                        </template>
                                        <th class="text-12 pb-16 font-bold text-gray-500" v-if="product.volume_discounts == null || product.volume_discounts.length == 0">Prijs</th>
                                        <template v-else>
                                            <th class="text-12 pb-16 font-bold text-gray-500" v-for="(discount, index) in product.volume_discounts" :key="index">{{ index + 1 === product.volume_discounts.length ? `>${discount.from} stuks` : `${discount.from} - ${product.volume_discounts[index + 1].from - 1} stuks` }}</th>
                                        </template>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(variant, index) in product.variants" :key="index" :class="{ 'bg-gray-100': index % 2 !== 0 }">
                                        <template v-for="option in Object.keys(mappedOptions)">
                                            <td class="text-14 first:pl-8 py-16" :key="`${variant.ean}_${option}`">{{ variant.content[parseOption(option).key] }}</td>
                                        </template>
                                        <td class="text-14 py-16" v-if="product.volume_discounts == null || product.volume_discounts.length == 0" :key="`${variant.ean}_price`">&euro; {{ $store.getters['pricesIncludingVat'] ? formatCurrency(variant.price_vat_incl) : formatCurrency(variant.price) }}</td>
                                        <template v-else>
                                            <td class="text-14 py-16" v-for="volume in variant.volume_prices" :key="`${variant.ean}_volume_${volume.from}_price`">&euro; {{ $store.getters['pricesIncludingVat'] ? formatCurrency(volume.price_vat_incl) : formatCurrency(volume.price) }}</td>
                                        </template>
                                    </tr>
                                </tbody>
                            </table>
                            <span class="text-12 pb-10 font-semibold text-gray-400">{{ $store.getters['pricesIncludingVat'] ? $t('default.prices_including_vat') : $t('default.prices_excluding_vat') }}</span>
                        </div>
                    </div>
                </div>

                <div class="container">
                    <div class="-mx-30 -mb-60 lg:-mb-80 flex flex-wrap">
                        <template v-for="(section, index) in orderedSections">
                            <div class="px-30 mb-60 lg:mb-80 w-full" v-if="section.type === 'description'" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 mb-16">{{ section.content.title != '' ? section.content.title : translations.description }}</h2>

                                <div class="row">
                                    <div class="lg:w-7/12 w-full">
                                        <p class="wysiwyg text-gray-500" v-html="section.content.description"></p>
                                    </div>
                                </div>
                            </div>

                            <div class="px-30 mb-60 lg:mb-80 w-full" v-if="section.type === 'related_products' && section.products.length > 0" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 mb-32">{{ section.content.title != '' ? section.content.title : translations.related_products }}</h2>

                                <div class="-mx-30 -mb-60 flex flex-wrap">
                                    <div class="px-30 mb-60 md:w-1/2 w-full" v-for="product in section.products" :key="product.id">
                                        <associated-product :product-id="product"></associated-product>
                                    </div>
                                </div>
                            </div>

                            <div class="px-30 mb-60 lg:mb-80 w-full" v-if="section.type === 'comparable_products' && section.products.length > 0" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 lg:mb-32 mb-24">{{ section.content.title != '' ? section.content.title : translations.comparable_products }}</h2>

                                <div class="row -mb-32">
                                    <div class="lg:w-1/4 md:w-1/3 flex w-1/2 mb-32" v-for="relatedProduct in section.products" :key="relatedProduct.id">
                                        <lazy-product-card :product-id="relatedProduct"></lazy-product-card>
                                    </div>
                                </div>
                            </div>

                            <div class="px-30 mb-60 lg:mb-80 w-full" v-if="section.type === 'technicals'" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 mb-32">{{ section.content.title != '' ? section.content.title : translations.technicals }}</h2>

                                <div class="-mx-30 flex flex-wrap">
                                    <div class="px-30 lg:w-1/2 lg:mb-0 w-full mb-40">
                                        <div class="wysiwyg leading-relaxed text-gray-500" v-html="section.content.description_1"></div>
                                    </div>
                                    <div class="px-30 lg:w-1/2">
                                        <div class="wysiwyg leading-relaxed text-gray-500" v-html="section.content.description_2"></div>
                                    </div>
                                </div>
                            </div>

                            <div class="px-30 mb-60 lg:mb-80 w-full" v-if="section.type === 'media'" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 mb-32">{{ section.content.title != '' ? section.content.title : translations.media }}</h2>

                                <video-section :media="section.media"></video-section>
                            </div>

                            <div class="px-30 md:w-1/2 mb-60 lg:mb-80 w-full" v-if="section.type === 'placement'" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 mb-32">{{ section.content.title != '' ? section.content.title : translations.placement_info }}</h2>
                                <video-section :media="section.media" :single="true"></video-section>
                            </div>

                            <div class="px-30 md:w-1/2 mb-60 lg:mb-80 w-full" v-if="section.type === 'manuals'" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <h2 class="h4 mb-32">{{ section.content.title != '' ? section.content.title : translations.documents }}</h2>
                                <download-list :media="section.media"></download-list>
                            </div>

                            <div class="px-30 mb-60 lg:mb-80 w-full" v-if="section.type === 'reviews' && section.reviews != null && section.reviews.length > 0" :key="`${section.type}_${index}`" :id="`${section.type}_${index}`">
                                <reviews :reviews="section.reviews" :translations="reviewData"></reviews>
                            </div>

                            <template v-if="section.type === 'questions'">
                                <div class="px-30 lg:w-1/2 mb-60 lg:mb-80 w-full" :key="`${section.type}_form_${index}`" :id="`${section.type}_${index}`">
                                    <h2 class="h4 mb-32">{{ translations.ask_question }}</h2>
                                    <contact-form :product="product.slug" :translations="formData"></contact-form>
                                </div>
                                <div class="px-30 lg:w-1/2 mb-60 lg:mb-80 w-full" :key="`${section.type}_faq_${index}`">
                                    <h2 class="h4 lg:mb-32 mb-24">{{ translations.faq }}</h2>
                                    <faq :faq-data="faqData"></faq>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
            </div>
            <div key="loading" v-else class="flex items-center justify-center flex-1">
                <spinner></spinner>
            </div>
        </transition>
    </div>
</template>

<script>
import Reviews from './Reviews.vue';
import { bus } from '@/app';

export default {
    components: { Reviews },
    name: 'Ui.ProductDetail',

    props: {
        productData: {
            type: Object,
            required: true,
        },

        faqData: {
            type: Array,
            required: true,
        },

        formData: {
            type: Object,
            required: true,
        },

        translations: {
            type: Object,
            required: true,
        },

        reviewData: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            ready: false,
            product: null,
            sections: null,
            activeVariant: null,
            activeVariantSurface: null,
            primaryCategory: null,
            addAmount: 1,
            localSurface: null,
            applyCuttingLoss: false,
        };
    },

    mounted() {
        this.product = this.productData;

        if (this.product.variants.length > 0) this.activeVariant = this.product.variants[0];

        let promises = [this.getProductSections(this.product.id)];

        if (this.product.collections_total > 0) {
            promises.push(this.getPrimaryCategory(this.product.id));
        }

        Promise.all(promises).then(
            () => {
                this.ready = true;
            },
            function (error) {
                console.log(error);
            },
        );
    },

    methods: {
        getProductSections(slug) {
            const self = this;
            return new Promise(function (resolve, reject) {
                axios
                    .get(`/api/product-sections/${slug}`)
                    .then(function (response) {
                        self.sections = response.data.sort((a, b) => {
                            return a > b;
                        });
                        resolve(response.data);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },

        getPrimaryCategory(productId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`/api/categories?product_id=${productId}&per_page=1`)
                    .then((response) => {
                        this.primaryCategory = response.data[0];
                        resolve(response.data);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },

        increaseAmount() {
            this.addAmount++;
        },

        decreaseAmount() {
            if (this.addAmount > 1) {
                this.addAmount--;
            }
        },

        addToCart() {
            this.$store.dispatch('addProduct', {
                productData: this.product,
                variant: this.activeVariant,
                amount: parseInt(this.addAmount),
            });

            bus.$emit('slideout', 'shopping-basket');
            bus.$emit('shopping-basket-message', 'Je product is toegevoegd');
        },

        getPossibleOptions(option) {
            const self = this;
            let optionvalues = [];

            const optionKey = option.key;

            this.product.variants.forEach((v) => {
                if (optionvalues.indexOf(v.content[optionKey]) < 0) {
                    optionvalues.push(v.content[optionKey]);
                }
            });

            const possibleOptionValues = optionvalues.filter((value) => {
                return self.optionValueIsPossible(option, value);
            });

            return possibleOptionValues;
        },

        formatCurrency(amount) {
            let val = (amount / 1).toFixed(2).replace('.', ',');
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },

        changeVariant(option, value) {
            const indexOfOption = this.productOptionsWithoutOppCalc.indexOf(option);
            let optionsToMatch = JSON.parse(JSON.stringify(this.productOptionsWithoutOppCalc));
            optionsToMatch.splice(indexOfOption + 1);

            let variantToMatch = JSON.parse(JSON.stringify(this.activeVariant));

            let matchedVariant = this.product.variants.find((v) => {
                let match = optionsToMatch.every((o) => {
                    if (option.key === o.key) {
                        return v.content[o.key] === value;
                    } else {
                        return v.content[o.key] === variantToMatch.content[o.key];
                    }
                });
                return match;
            });

            // If no variant is found, try to find a variant with the same value for the option
            if (!matchedVariant) {
                matchedVariant = this.product.variants.find((v) => {
                    return v.content[option.key] === value;
                });
            }

            if (matchedVariant) this.activeVariant = matchedVariant;
        },

        optionValueIsPossible(option, value) {
            if (this.filterableProductOptionsWithoutOppCalc.length === 1) {
                return true;
            }

            const indexOfOption = this.productOptionsWithoutOppCalc.indexOf(option);
            let optionsToMatch = JSON.parse(JSON.stringify(this.productOptionsWithoutOppCalc));

            optionsToMatch.splice(indexOfOption + 1);

            let variantToMatch = JSON.parse(JSON.stringify(this.activeVariant));

            let matchedVariants = this.product.variants.filter((v) => {
                let match = optionsToMatch.every((o) => {
                    if (option.key === o.key) {
                        return v.content[o.key] === value;
                    } else {
                        return v.content[o.key] === variantToMatch.content[o.key];
                    }
                });
                return match;
            });

            return matchedVariants.length > 0;
        },

        updateActiveVariantSurface() {
            this.activeVariantSurface = this.activeVariant.content[Object.keys(this.activeVariant.content).find((k) => k.indexOf('opp_calc') >= 0)];
        },

        calculateNeededAmount() {
            const inputSurface = parseFloat(this.localSurface);
            if (!isNaN(inputSurface)) {
                this.addAmount = Math.ceil(inputSurface / this.activeVariantSurface);
            }
        },

        calculateSurfaceOfAmount() {
            const surface = this.applyCuttingLoss ? Math.round(this.activeVariantSurface / 1.1) : this.activeVariantSurface;
            this.localSurface = this.addAmount * surface;
        },

        parseOption(mappedOption) {
            return this.product.options.find((o) => o.name === this.mappedOptions[mappedOption]);
        },

        getActiveVariantPrice(vat) {
            if (this.activeVariant != null) {
                let price = null;
                if (this.activeVariant.volume_prices != null && this.activeVariant.volume_prices.length > 0) {
                    for (let i = this.activeVariant.volume_prices.length - 1; i >= 0; i--) {
                        const volume_discount = this.activeVariant.volume_prices[i];

                        if (this.addAmount >= volume_discount.from) {
                            price = vat ? volume_discount.price_vat_incl : volume_discount.price;
                            break;
                        }
                    }
                } else {
                    price = vat ? this.activeVariant.price_vat_incl : this.activeVariant.price;
                }
                let val = (price / 1).toFixed(2).replace('.', ',');
                return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            }
            return null;
        },
    },

    computed: {
        orderedSections() {
            const orderedSections = this.sections.sort((a, b) => {
                if (a.order < b.order) {
                    return -1;
                }
                if (a.order > b.order) {
                    return 1;
                }
                return 0;
            });
            return orderedSections;
        },

        mappedOptions() {
            // product options in legacy format
            let options = {};

            if (this.product && this.productOptionsWithoutOppCalc) {
                let localOptions = this.productOptionsWithoutOppCalc;
                localOptions.sort((a, b) => {
                    if (a.ordinal < b.ordinal) return -1;
                    if (a.ordinal > b.ordinal) return 1;
                    return 0;
                });
                for (let i = 0; i < localOptions.length; i++) {
                    options[`option_${i + 1}`] = localOptions[i].name;
                }
            }

            return options;
        },

        productOptionsWithoutOppCalc() {
            return this.product.options.filter((o) => o.type !== 'opp_calc');
        },

        filterableProductOptionsWithoutOppCalc() {
            return this.product.options.filter((o) => o.type !== 'opp_calc' && o.filterable);
        },

        orderedMedia() {
            const videos = this.product.media.filter((m) => {
                return m.type.indexOf('link') >= 0;
            });

            const images = this.product.media.filter((m) => {
                return m.type.indexOf('link') < 0;
            });

            let customArray = [];

            if (images.length > 0) {
                customArray.push(images[0]);
                images.shift();
            }

            customArray = customArray.concat(videos);
            customArray = customArray.concat(images);

            return customArray;
        },

        breadcrumbs() {
            let breadcrumbs = [
                {
                    title: 'Aanbod',
                    link: '/producten',
                },
            ];

            if (this.primaryCategory != null) {
                breadcrumbs.push({
                    title: this.primaryCategory.name,
                    link: `/producten/${this.primaryCategory.slug}`,
                });
            }

            breadcrumbs.push({
                title: this.product.name,
            });

            return breadcrumbs;
        },

        activeVariantInStock() {
            if (this.activeVariant != null) {
                if (this.activeVariant.stock_block != null) {
                    return this.activeVariant.stock > parseInt(this.activeVariant.stock_block);
                } else {
                    return this.activeVariant.stock > 0;
                }
            }
            return false;
        },

        firstQuestionsSections() {
            return this.orderedSections.find((s) => {
                return s.type === 'questions';
            });
        },

        showSurfaceCalculator() {
            let showSurfaceCalculator = false;

            if (this.product.options.find((o) => o.key.indexOf('opp_calc') >= 0)) showSurfaceCalculator = true;

            return showSurfaceCalculator;
        },
    },

    watch: {
        activeVariant() {
            if (this.showSurfaceCalculator) {
                this.updateActiveVariantSurface();
            }
        },

        applyCuttingLoss(applyCuttingLoss) {
            if (applyCuttingLoss) {
                this.localSurface = Math.round(this.localSurface * 1.1);
            } else {
                this.localSurface = Math.round(this.localSurface / 1.1);
            }
            this.calculateNeededAmount();
        },

        activeVariantSurface(newQuestion, oldQuestion) {
            this.localSurface = this.activeVariantSurface;
        },
    },
};
</script>
